import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Box,
    useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone } from 'react-dropzone';

import upload from 'packages/novous_web/src/assets/icons/upload-04.svg';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import InputField from 'packages/novous_web/src/common/components/InputField';
import Button from 'packages/novous_web/src/common/components/Button';
import file from 'packages/novous_web/src/assets/icons/Image File.svg';
import useApiError from 'packages/novous_web/src/api/useApiError';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import AlertToast from 'packages/novous_web/src/common/components/Alert';

interface UploadDeviceApkProps {
    open: boolean;
    handleClose: () => void;
    // onUpload: (formData: FormData) => void;
}

interface EmailApiResponse {
    data: string;
    error: string;
    message: string;
    status: number;
}

const formatFileSize = (sizeInBytes: number): string => {
    if (sizeInBytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
    return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const UploadDeviceApk: React.FC<UploadDeviceApkProps> = ({ open, handleClose }) => {
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<"error" | "success" | "warning" | "info">("error");
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState('');
    const [versionNumber, setVersionNumber] = useState('');
    const [versionCode, setVersionCode] = useState('');
    const [commitId, setCommitId] = useState('');
    const [releaseNote, setReleaseNote] = useState('');
    const { user } = useAuth();
    const theme = useTheme();

    const {
        data: deviceUpload,
        error: deviceUploadError,
        loading: deviceUploadLoading,
        fetchData: fetchDeviceUploadData,
    } = useApiError<any>(`${API_ENDPOINTS.users.DEVICE_UPLOAD_APK}`, "POST");

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            const apkFile = acceptedFiles[0];

            setFile(apkFile);
            if (!fileName) {
                setFileName(apkFile.name.replace('.apk', ''));
            }
        }
    }, [fileName]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.android.package-archive': ['.apk']
        },
        maxFiles: 1,
        multiple: false
    });

    const handleSubmit = async () => {
        setIsSendingEmail(true);
        const parsedVersionCode = parseInt(versionCode, 10);
        if (isNaN(parsedVersionCode)) {
            console.error("Invalid Version Code, please enter a number.");
            return;
        }

        const formData = new FormData();

        if (file) {
            formData.append('files', file);
        }
        formData.append('fileName', fileName);
        formData.append('versionNumber', versionNumber);
        formData.append('versionCode', parsedVersionCode.toString());
        formData.append('releaseNote', releaseNote);
        formData.append('commitId', commitId);

        formData.forEach((value, key) => {
            if (value instanceof File) {
                console.log(`${key}: [File] name=${value.name}, size=${value.size}, type=${value.type}`);
            } else {
                console.log(`${key}: ${value}`);
            }
        });

        try {
            const response = await fetchDeviceUploadData(formData);
            if (response?.status === 201 || response?.status === 200) {
                setAlertMessage(response?.message);
                setAlertType("success");
                setAlertOpen(true);
            } else {
                setAlertMessage(response?.error);
                setAlertType("error");
                setAlertOpen(true);
            }
        } catch (error) {
            console.error("Error sending email:", error);
            setAlertMessage("An unexpected error occurred during upload.");
            setAlertType("error");
        } finally {
            setAlertOpen(true);
            setIsSendingEmail(false);
        }
    };


    const handleReset = () => {
        setFile(null);
        setFileName('');
        setVersionNumber('');
        setVersionCode('');
        setCommitId('');
        setReleaseNote('');
    };

    const removeFile = () => {
        setFile(null);
    };

    return (
        <>
            <AlertToast message={alertMessage} type={alertType} open={alertOpen} onClose={() => setAlertOpen(false)} />
            <Dialog open={open} onClose={() => {
                if (!isSendingEmail) {
                    handleClose();
                }
            }} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Upload APK File</Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={3} py={2}>
                        {/* Dropzone Area */}
                        <div
                            {...getRootProps()}
                            style={{
                                border: '2px dashed #ccc',
                                borderRadius: '8px',
                                padding: '32px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                backgroundColor: isDragActive ? '#f5f5f5' : '#fff',
                                transition: 'background-color 0.3s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '8px',
                                minHeight: '150px'
                            }}
                        >
                            <input {...getInputProps()} />
                            {!file ? (
                                <>
                                    <div className="flex gap-1 items-center justify-center">
                                        <img src={upload} alt="upload" width={24} height={24} />
                                        <Typography variant="overline" color={theme.palette.primary.main} fontWeight={700}>
                                            {isDragActive ? 'Drop the APK file here' : 'Drag & drop APK file or click to select'}
                                        </Typography>
                                    </div>
                                    <Typography variant="caption" color="textSecondary">
                                        Only .apk files are accepted
                                    </Typography>
                                </>
                            ) : (
                                <UploadedFile
                                    name={file.name}
                                    size={file.size}
                                    onRemove={removeFile}
                                />
                            )}
                        </div>

                        <Box className="grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 gap-4">
                            {/* File Name */}
                            <InputField
                                label="File Name"
                                name="fileName"
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                placeholder="Enter File Name"
                                type="text"
                                isMandatory={true}
                                isDisabled
                            />

                            {/* Version Number */}
                            <InputField
                                label="Version Number"
                                value={versionNumber}
                                onChange={(e) => setVersionNumber(e.target.value)}
                                placeholder="Enter Version Number"
                                type="text"
                                isMandatory={true}
                            />

                            {/* Version Code */}
                            <InputField
                                label="Version Code"
                                value={versionCode}
                                onChange={(e) => setVersionCode(e.target.value)}
                                placeholder="Enter Version Code"
                                type="text"
                                isMandatory={true}
                            />

                            {/* Commit ID */}
                            <InputField
                                label="Commit ID"
                                value={commitId}
                                onChange={(e) => setCommitId(e.target.value)}
                                placeholder="Enter Commit Id"
                                type="text"
                                isMandatory={true}
                            />
                        </Box>
                        <Box className="grid grid-cols-1 gap-4">
                            {/* Release Notes */}
                            <InputField
                                label="Release Notes"
                                value={releaseNote}
                                onChange={(e) => setReleaseNote(e.target.value)}
                                placeholder="Enter Release Note"
                                type="text"
                                isMandatory={true}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box display="flex" justifyContent="space-between" width="100%" px={2} py={1}>
                        <Button
                            text='Cancel'
                            variant='outlined'
                            onClick={() => {
                                handleClose();
                                handleReset();
                            }}
                            fullWidth={false}
                            disabled={isSendingEmail}
                        />
                        <Button
                            text={isSendingEmail ? 'Uploading...' : 'Upload'}
                            onClick={handleSubmit}
                            fullWidth={false}
                            variant="contained"
                            disabled={!file || !fileName || !versionNumber || !versionCode || isSendingEmail}
                        />
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UploadDeviceApk;

interface UploadedFileProps {
    name: string;
    size: number;
    onRemove: () => void;
}

const UploadedFile: React.FC<UploadedFileProps> = ({ name, size, onRemove }) => {
    return (
        <div className="flex p-4 gap-4 w-full items-center border rounded-md relative">
            <img src={file} alt="file" width={32} height={32} />
            <div className="flex flex-col gap-1 w-full overflow-x-hidden">
                <Typography className="truncate" variant="body2" fontWeight={400}>
                    {name}
                </Typography>
                <Typography variant="body2" color="secondary" fontWeight={400}>
                    {formatFileSize(size)}
                </Typography>
            </div>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                }}
                sx={(theme) => ({
                    position: 'absolute',
                    top: -12,
                    right: -10,
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.common.white,
                    '&:hover': {
                        backgroundColor: theme.palette.error.dark,
                    },
                    borderRadius: '20%',
                    padding: '2px',
                })}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </div>
    );
};